import { excludedOrganizationRefs as exclusionsForUser, isExcludedOrganization } from "../../components/Admin/Scheduling/organizationScope";
import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import { useOrgQuery } from "./useOrgQuery";
import { useCategoryOrganizations } from "./useCategoryOrganizations";

const ORG_STORAGE_KEY = "scheduling-organizationRef";
const ORG_CHANGED = "scheduling-organization-changed";
const subscribe = (listener) => {
  window.addEventListener(ORG_CHANGED, listener);
  window.addEventListener("storage", listener);
  return () => { window.removeEventListener(ORG_CHANGED, listener); window.removeEventListener("storage", listener); };
};

function normalizeTimeZone(timeZone) {
  const candidates = typeof timeZone === "string" ? [timeZone] : [timeZone?.value, timeZone?.name];
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const zone = moment.tz.zone(candidate.trim());
    if (zone) return zone.name;
  }
  return "";
}

function readStoredOrg(key, fallback) {
  try {
    return sessionStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

export function useSchedulingOrg() {
  const user = useSelector((state) => state?.auth?.user);
  const isSuperAdmin = Array.isArray(user?.["cognito:groups"]) && user["cognito:groups"].includes("Super-Admin");
  const lockedOrgId = user?.["custom:orgId"] || user?.organizations?.[0]?.id || "";
  const lockedOrgName = user?.organizations?.find(org => org.id === lockedOrgId)?.name || "";
  const practitionerRef = user?.["custom:practitioner_id"] || "";
  const storageKey = `${ORG_STORAGE_KEY}:${user?.sub || practitionerRef || "admin"}:${lockedOrgId}`;
  const snapshot = useCallback(() => readStoredOrg(storageKey, lockedOrgId), [storageKey, lockedOrgId]);
  const selectedOrgId = useSyncExternalStore(subscribe, snapshot, () => lockedOrgId);

  const { data: orgData, isLoading: rootLoading, isError: rootError, refetch: refetchRoot } = useOrgQuery({
    orgId: isSuperAdmin ? null : lockedOrgId,
    enabled: isSuperAdmin || !!lockedOrgId,
    refetchOnWindowFocus: false,
  });
  const organizations = useMemo(() => Array.isArray(orgData) ? orgData :
    [orgData?.organization || orgData].filter((org) => org?.id), [orgData]);

  const exclusions = useMemo(() => exclusionsForUser(user), [user]);
  const baseOptions = useMemo(
    () =>
      (organizations || []).filter(org => org.active !== false && !isExcludedOrganization(org.id, exclusions)).map((org) => ({
        label: org?.name,
        name: org?.name,
        value: org?.id,
        timeZone: org?.timeZone,
      })),
    [organizations, exclusions]
  );

  const owners = useCategoryOrganizations({ isSuperAdmin, organizationRef: lockedOrgId, organizations,
    excludedOrganizationRefs: exclusions.join(","), orgOptions: baseOptions, orgsLoading: rootLoading,
    orgsError: rootError, refetchOrgs: refetchRoot });
  const allOrganizations = useMemo(() => [...organizations, ...(owners.organizations || [])], [organizations, owners.organizations]);
  const orgOptions = useMemo(() => owners.options.map(option => ({ ...option,
    timeZone: allOrganizations.find(org => org.id === option.value)?.timeZone })), [owners.options, allOrganizations]);
  const organizationRef = orgOptions.some(org => org.value === selectedOrgId) ? selectedOrgId :
    owners.isLoading && selectedOrgId !== lockedOrgId ? "" :
    orgOptions.some(org => org.value === lockedOrgId) || rootLoading ? lockedOrgId : "";
  const setSelectedOrgId = useCallback((id) => {
    if (!orgOptions.some(org => org.value === id)) return;
    try { sessionStorage.setItem(storageKey, id); window.dispatchEvent(new Event(ORG_CHANGED)); } catch { /* private mode */ }
  }, [orgOptions, storageKey]);
  const selectedOrg = orgOptions.find((org) => org.value === organizationRef);
  const organizationName = selectedOrg?.label || lockedOrgName;
  const organizationTimeZone = normalizeTimeZone(selectedOrg?.timeZone ||
    user?.organizations?.find((org) => org.id === organizationRef)?.timeZone);

  return {
    user,
    excludedOrganizationRefs: exclusions.join(","),
    isSuperAdmin,
    lockedOrgId,
    lockedOrgName,
    practitionerRef,
    organizationRef,
    organizationName,
    organizationTimeZone,
    selectedOrgId: organizationRef,
    setSelectedOrgId,
    orgOptions,
    orgsLoading: owners.isLoading,
    orgsError: owners.isError,
    refetchOrgs: owners.retry,
    organizations: allOrganizations,
  };
}
